<script>
    import { onMount } from 'svelte';
    import { fly } from 'svelte/transition';

    import { lang } from './language';
    import { mods, events, reload, disabled } from './mods/modules';
    import { baseData } from './helpers/data';

    import { calculateAge } from './helpers/calculateAge';
    import { getDateMin, getDay, getMonthNumber, getFullYear } from './helpers/date';
    import { configCommunities } from './mods/configCommunities';
    import { countries } from './countries';
    import { nextButtonVisibility } from './mods/nextButtonVisibility';

    import CTA from './components/cta.svelte';
    import GENDER from './components/gender.svelte';
    import BIRTHDATE from './components/birthdate.svelte';
    import FIRSTNAME from './components/name.svelte';
    import COUNTRY from './components/country.svelte';
    import EMAIL from './components/emailConsent.svelte';
    import UPLOAD from './components/upload.svelte';
    import AJAX from './components/ajax.svelte';
    import VIGNETTE from './components/vignette.svelte';

    import './global.css';

    //REGION CONSTANTS
    const multistepConfig = 'multistep-config';

    //REGION FUNCTIONS
    /**
     * return the widget type for a step
     * @param step
     * @param data
     */
    function getType(step, data) {
        return mods[data.steps[step]?.type];
    }

    /**
     * get i18n value
     */
    function getI18n(data) {
        return !!data.global?.options?.community == true ? data.global.options.community : 'en';
    }

    function getMultistepConfig() {
        Maestro.getInstance()
            .Store.loadAsync(multistepConfig)
            .then((config) => {
                if (Object.keys(config).length) data = config;
                store.i18n = getI18n(data);
                // when config is received, the country selected is the first one of the top country from the top countries
                // map to build full country object based on the iso code from topCountry and using countries from country variable
                store.country = configCommunities[store.i18n].topCountries.map(
                    (iso) => countries.find((country) => country.iso === iso)
                    // take the first one (index 0)
                )[0];
                stepResolver(0);
            });
    }

    function setEventListener(step, data) {
        // EVENT LISTENERS ON EACH WIDGET SELECTION
        Maestro.getInstance().EventBus.on(events[getType(step, data)], function () {
            Maestro.getInstance().EventBus.emit(`${getType(step, data)}-stop`);
            stepResolver(step + 1);
        });
    }

    function emitEvent(event, data) {
        Maestro.getInstance().EventBus.emit(event, data);
    }

    function stopModuleOnLastStep(data, step) {
        if (data.steps.length < step + 1) {
            emitEvent(`${getType(step + 1, data)}-stop`);
        }
    }

    function getWidgetParams(data, step) {
        return reload.includes(mods[data.steps[step].type])
            ? JSON.parse(JSON.stringify(data.steps[step].config))
            : undefined;
    }

    function startNotDisabledWidgets(data, step) {
        // !data.steps[step].disabled -- step is disabled when user action has been made ex: drawn a card
        // if is a widget && is not disabled
        if (mods[data.steps[step].type] && !data.steps[step].disabled) {
            emitEvent(`${mods[data.steps[step].type]}-start`, getWidgetParams(data, step));
        } // resume module (and send new config if available)
    }

    function launchAjax(step, store) {
        //FORMAT OPTIONS OBJECT
        let options = {
            birthdate: new Date(store.birthdate),
            brandConsent: store.brandConsent,
            community: store.i18n,
            country: store.country,
            email: store.email,
            gender: store.gender,
            name: store.firstname,
            partnerOptin: store.optin,
            secondBrand: store.secondBrandConsent,
            vignette: store.vignette,
        };
        // LAUNCH THE CONFIG FUNCTION GIVEN BY THE DATA
        data.steps[step].config.ajax(JSON.parse(JSON.stringify(options))).then(() => {
            console.log('ok');
        });
    }

    function goToPreviousStepIfDisabledStep(data, step, store) {
        if (step != 0 && disabled[data.steps[store.nav.current - 1].type]) {
            data.steps[store.nav.current - 1].disabled = true;
        }
    }

    function goToNextStepIfDisabledStep(data, step, store) {
        if (data.steps[step].disabled) {
            stepResolver(store.nav.current + 1);
        } // Continue to the next step if the current step is disabled
    }

    // FUCTION FOR THE STEP NAVIGATION
    function stepResolver(step) {
        store.step = data.steps[step];

        // stop module on last step
        stopModuleOnLastStep(data, step);

        // move to the new step
        store.nav.current = step;

        //launch start event when on a not disabled widget step
        startNotDisabledWidgets(data, step);

        if (data.steps[step].type === 'ajax') {
            launchAjax(step, store);
        } // start registration process

        goToPreviousStepIfDisabledStep(data, step, store);
        goToNextStepIfDisabledStep(data, step, store);

        setEventListener(store.nav.current, data);
    }

    // FUNCTION BACK
    function backintime(step) {
        if (!data.steps[step]) {
            return false;
        }
        if (data.steps[step].disabled) {
            return backintime(step - 1);
        }
        store.error = false;
        stepResolver(step);
    } // Go back to the previous enabled step

    // VALIDATION OF STEPS
    function validate(type) {
        switch (type) {
            case 'birth-date':
                const birthdate = Date.parse(`${store.birth.year}-${store.birth.month}-${store.birth.day}`);
                if (!isNaN(birthdate) && calculateAge(birthdate) >= 18) {
                    store.birthdate = `${store.birth.year}-${store.birth.month}-${store.birth.day}`;
                    store.error = false;
                    stepResolver(++store.nav.current);
                } else {
                    store.error = data.steps[store.nav.current]?.config.form.label.tooYoungError;
                }
                break;
            case 'gender':
                if (store.gender) {
                    stepResolver(++store.nav.current);
                    store.error = false;
                } else {
                    store.error = lang[store.i18n].genderError;
                }
                break;
            case 'name':
                if (store.firstname) {
                    stepResolver(++store.nav.current);
                    store.error = false;
                } else {
                    store.error = lang[store.i18n].firstnameError;
                }
                break;
            case 'country':
                if (store.country) {
                    store.error = false;
                    stepResolver(++store.nav.current);
                }
                break;
            case 'email-consent':
                if (store.brandConsent && store.email) {
                    store.error = false;
                    stepResolver(++store.nav.current);
                } else {
                    store.error = '';
                    if (!store.brandConsent) {
                        store.error += lang[store.i18n].consentError + '<br/>';
                    }
                    if (!store.email) {
                        store.error += store.error = lang[store.i18n].emailError + '<br/>';
                    }
                }
                break;
            case 'vignette':
                if (store.vignette) {
                    store.error = false;
                    stepResolver(++store.nav.current);
                }
                break;
            case 'cta':
                const uploadFile = data.steps[store.nav.current]?.config?.uploadFile;
                if ((uploadFile && store.upload.image) || !uploadFile) stepResolver(++store.nav.current);
                break;
            default:
                store.error = false;
                stepResolver(++store.nav.current);
        }
    }

    //ENREGION FUNCTIONS

    // BUTTON VISIBILITY - Steps on which it is displayed
    const next = nextButtonVisibility;

    //Base data initializing
    let data = baseData;

    // CALCULATION OF THE BIRTH DATE MINIMAL DATE
    const dateMin = getDateMin();

    let day = getDay(dateMin);
    let month = getMonthNumber(dateMin);
    let yearI = getFullYear(dateMin);

    // STORE INITIAL OBJECT -- WILL BE REPLACE BY THE CONFIG
    let store = {
        nav: {
            current: 0,
        },
        i18n: getI18n(data),
        gender: '',
        birthdate: '',
        birth: {
            day: day,
            month: month,
            year: yearI,
        },
        country: {},
        firstname: '',
        email: '',
        optin: false,
        brandConsent: false,
        secondBrandConsent: false,
        vignette: '',
        upload: {
            image: '',
        },
    }; // empty store

    // Load data with maestro
    onMount(async () => {
        getMultistepConfig();
    });

    // BANNER FUNCTIONNALITY
    const hasBanner = typeof data.global.banner !== 'undefined' && typeof data.global.banner?.url === 'string';
    const bannerSrc = data.global.banner?.url;
    const bannerAlt = data.global.banner?.alt;

    //FUNCTION FOR CHANGES
    const changeGender = (event) => {
        store.gender = event.detail;
    };
    // ACTIONS ON DATE CHANGES
    const changeDay = (event) => {
        store.birth.day = event.detail;
    };
    // ACTIONS ON MONTH CHANGES
    const changeMonth = (event) => {
        store.birth.month = event.detail;
    };
    // ACTIONS ON YEAR CHANGES
    const changeYear = (event) => {
        store.birth.year = event.detail;
    };
    // ACTIONS ON NAME CHANGES
    const changeName = (event) => {
        store.firstname = event.detail;
    };
    // ACTIONS ON COUNTRY CHANGES
    const changeCountry = (event) => {
        store.country = event.detail;
    };
    // ACTIONS ON EMAIL CHANGES
    const changeEmail = (event) => {
        store.email = event.detail;
    };
    // ACTIONS ON BRAND CONSENT CHANGES
    const changeBrandConsent = (event) => {
        store.brandConsent = event.detail;
    };
    // ACTIONS ON SECOND BRAND CHANGES
    const changeSecondBrandConsent = (event) => {
        store.secondBrandConsent = event.detail;
    };
    // ACTIONS ON PARTNER OPTIN CHANGES
    const changePartnerOptin = (event) => {
        store.optin = event.detail;
    };
    // ACTIONS ON VIGNETTE CHANGES
    const changeVignette = (event) => {
        store.vignette = event.detail;
    };
    //const i18n = !!data.global?.options?.community == true ? data.global.options.community : 'en';

    for (let country of countries) {
        if (country.iso == configCommunities[store.i18n].country) {
            store.country = country;
        }
    }
</script>

<!-- ======================== HTML TEMPLATE ================================= -->
<div class="app-msi_container">
    <div class="app-msi_flex-column">
        <!-- IF THE CONFIG CONTAINS A BANNER CONFIGURATION AND A BANNER URL, DISPLAY THIS -->
        {#if hasBanner}
            <div class="app-msi_banner-container">
                <img src={bannerSrc} alt={bannerAlt} class="app-msi_banner-img" />
            </div>
        {/if}
        <!-- IF THE STEP IS GREATER THAN 2 OR IF THE MULTISTEP IS IN STANDALONE AND STEP IS 1 -->
        {#if store.nav.current >= 2 || (store.nav.current == 1 && !mods[data.steps[0].type])}
            <div
                class="app-msi_text-center app-msi_step"
                on:click={() => {
                    backintime(store.nav.current - 1);
                }}
            >
                <div class="app-msi_back-button">&#60;</div>
            </div>
        {/if}
        <!-- IF THE CONFIG CONTAINS A BANNER CONFIGURATION AND A BANNER URL, DISPLAY THIS -->
        <!-- TITLE OF THE PAGE -->
        <div class="app-msi_text-center app-msi_title">
            {data.steps[store.nav.current].title}
        </div>
        <div>
            <ul>
                <!-- LOOP ON EACH STEP -->
                {#each data.steps as step, i}
                    <!-- IF THE STEP IS THE CURRENT ONE, DISPLAY THIS -->
                    {#if i == store.nav.current}
                        <li in:fly={{ x: 200, duration: 200 }} out:fly={{ x: -200, duration: 200 }}>
                            <!-- <svelte:component this={step.type}/> -->

                            {#if store.error}
                                <p class="error">{@html store.error}</p>
                            {/if}
                            <!-- CTA COMPONENT -->
                            {#if step.type == 'cta'}
                                <CTA htmlContent={step.config?.content} />
                                {#if step.config?.uploadFile}
                                    <UPLOAD
                                        skipUploadText={step.config?.skipText}
                                        on:image={(image) => (store.upload.image = image.detail)}
                                        on:skip={() => stepResolver(++store.nav.current)}
                                    />
                                {/if}
                            {/if}
                            <!-- GENDER COMPONENT -->
                            {#if step.type == 'gender'}
                                <GENDER
                                    maleLabel={data.form?.label?.male || lang[store.i18n].male}
                                    femaleLabel={data.form?.label?.female || lang[store.i18n].female}
                                    value={store.gender}
                                    on:gender={changeGender}
                                />
                            {/if}
                            <!-- BIRTH DATE COMPONENT -->
                            {#if step.type == 'birth-date'}
                                <BIRTHDATE
                                    dayLabel={data.form?.label?.day || lang[store.i18n].day}
                                    monthLabel={data.form?.label?.day || lang[store.i18n].month}
                                    yearLabel={data.form?.label?.day || lang[store.i18n].year}
                                    i18n={store.i18n}
                                    day={store.birth.day}
                                    month={store.birth.month}
                                    year={store.birth.year}
                                    on:day={changeDay}
                                    on:month={changeMonth}
                                    on:year={changeYear}
                                />
                            {/if}
                            <!-- FIRSTNAME COMPONENT -->
                            {#if step.type == 'name'}
                                <FIRSTNAME
                                    message={data.form?.label?.message || ''}
                                    placeholder={lang[store.i18n].name || data.form?.label?.field}
                                    value={store.firstname}
                                    on:name={changeName}
                                />
                            {/if}
                            <!-- COUNTRY COMPONENT -->
                            {#if step.type == 'country'}
                                <COUNTRY i18n={store.i18n} value={store.country.iso} on:country={changeCountry} />
                            {/if}
                            <!-- EMAIL CONSENT COMPONENT -->
                            {#if step.type == 'email-consent'}
                                <EMAIL
                                    placeholder={step.config.form?.label?.emailField || lang[store.i18n].emailField}
                                    brandLabel={step.config.form?.label?.brandConsentMessage}
                                    secondBrandTitle={step.config?.form?.label?.secondBrandTitle}
                                    secondBrandMessage={step.config?.form?.label?.secondBrandMessage}
                                    secondBrandAgreementMessage={step.config.form?.label?.secondBrandAgreementMessage}
                                    partnerBrandTitle={step.config?.form?.label?.partnerBrandTitle}
                                    partnerBrandMessage={step.config?.form?.label?.partnerBrandMessage}
                                    partnerBrandAgreementMessage={step.config.form?.label?.partnerBrandAgreementMessage}
                                    on:email={(event) => {
                                        changeEmail(event);
                                    }}
                                    on:brandConsent={(event) => {
                                        changeBrandConsent(event);
                                    }}
                                    on:secondBrandConsent={(event) => {
                                        changeSecondBrandConsent(event);
                                    }}
                                    on:partnerOptin={(event) => {
                                        changePartnerOptin(event);
                                    }}
                                />
                            {/if}
                            <!-- VIGNETTE COMPONENT -->
                            {#if step.type == 'vignette'}
                                <VIGNETTE
                                    community={store.i18n}
                                    openingText={step.openingText}
                                    resultText={step.resultText}
                                    direction={step.direction}
                                    items={step.items}
                                    value={store.vignette}
                                    on:vignette={changeVignette}
                                />
                            {/if}
                            <!-- UPLOAD COMPONENT -->
                            {#if step.type == 'upload'}
                                <UPLOAD on:image={(image) => (store.upload.image = image.detail)} />
                            {/if}
                            <!-- AJAX COMPONENT -->
                            {#if step.type == 'ajax'}
                                <AJAX
                                    message={step.config.form?.label?.message}
                                    loading="true"
                                    success="false"
                                    successLabel={step.config.validation?.label?.success}
                                    failLabel={step.config.validation?.label?.failed}
                                />
                            {/if}
                            <!-- BUTTON COMPONENT -->
                            {#if !mods[step.type] && next[step.type]}
                                <div class="app-msi_actions">
                                    <button
                                        on:click={() => validate(step.type)}
                                        class="app-msi_btn app-msi_btn-primary"
                                        type="submit"
                                    >
                                        <!-- if step is email, the submit label should appear, else the next label is appearing -->
                                        {#if step.type != 'email-consent'}
                                            {step.next ? step.next : data.global.label.next}
                                        {/if}
                                        {#if step.type == 'email-consent'}
                                            {step.next ? step.next : data.global.label.submit}
                                        {/if}
                                    </button>
                                </div>
                            {/if}
                        </li>
                    {/if}
                {/each}
            </ul>
        </div>
    </div>
</div>

<!-- FOR TESTING PURPOSE ONLY -- DISPLAY A WAY TO NAVIGATE BETWEEN STEPS WITHOUT USING THE FULL FORM -->
<!--
{#if store.nav.current >= 2 || (store.nav.current == 1 && !mods[data.steps[0].type])}
    <button
        on:click={() => {
            backintime(store.nav.current - 1);
        }}>ᐊ</button
    >
{/if}

<select bind:value={store.nav.current} on:change={() => stepResolver(store.nav.current)}>
    {#each data.steps as step, i}
        <option value={i}>{step.type}</option>
    {/each}
</select>

<pre><code>{JSON.stringify({ store }, null, 4)}</code></pre>

<style>
    code {
        display: block;
        margin: 50px;
    }
</style>
-->
